import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, RotateCcw } from 'lucide-react';
import BookCover from '../BookCover';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveCover = (src) => {
  if (!src) return null;
  if (src.startsWith('http') || src.startsWith('data:')) return src;
  if (src.startsWith('/api/')) return `${BACKEND_URL}${src}`;
  if (src.startsWith('/uploads/')) return `${BACKEND_URL}/api${src}`;
  return src;
};

const fmtTime = (sec) => {
  if (!Number.isFinite(sec) || sec <= 0) return null;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
};

/**
 * ResumeCard — single, prominent "pick up where you left off" card pinned
 * above the hero carousel. Surfaces the user's *most recent* in-progress
 * item (newest entry from the Continue Playing rail) with one big Play
 * button that jumps straight into /player/{id}?autoplay=1.
 *
 * Renders nothing for first-time users / users with no in-progress items —
 * Discover keeps its first-run experience clean.
 */
const ResumeCard = ({ rails }) => {
  const navigate = useNavigate();

  const item = useMemo(() => {
    const rail = (rails || []).find((r) => r.id === 'continue');
    return (rail?.items || [])[0] || null;
  }, [rails]);

  if (!item || !item.book_id) return null;

  const cover = resolveCover(item.cover_image);
  const pct = Math.max(2, Math.min(100, item.progress || 0));
  const remaining = (() => {
    if (!item.last_position || !item.duration) return null;
    const left = item.duration - item.last_position;
    return fmtTime(left);
  })();

  const handlePlay = () => {
    navigate(`/player/${item.book_id}?autoplay=1`);
  };

  return (
    <div
      className="px-4 sm:px-6 mb-5"
      data-testid="resume-card"
    >
      <button
        type="button"
        onClick={handlePlay}
        className="group relative w-full overflow-hidden rounded-2xl border border-amber-500/20 bg-gradient-to-r from-slate-900 via-slate-900 to-slate-950 text-left shadow-lg shadow-black/40 transition-all hover:border-amber-400/50 hover:shadow-amber-500/10"
        data-testid="resume-card-play"
        aria-label={`Resume ${item.title || 'last episode'}`}
      >
        {/* Blurred cover backdrop for depth */}
        {cover && (
          <div
            className="pointer-events-none absolute inset-0 opacity-25 blur-2xl scale-110"
            style={{
              backgroundImage: `url(${cover})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
            aria-hidden="true"
          />
        )}

        <div className="relative flex items-center gap-3 sm:gap-4 p-3 sm:p-4">
          {/* Cover thumbnail */}
          <div className="relative h-16 w-16 sm:h-20 sm:w-20 flex-shrink-0 overflow-hidden rounded-xl bg-slate-800 ring-1 ring-white/10">
            <BookCover
              book={item}
              className="h-full w-full"
              imageClassName="transition-transform duration-300 group-hover:scale-105 motion-reduce:transform-none"
            />
          </div>

          {/* Meta */}
          <div className="min-w-0 flex-1">
            <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-amber-400 flex items-center gap-1.5">
              <RotateCcw className="h-3 w-3" />
              Pick up where you left off
            </p>
            <h3
              className="mt-0.5 truncate text-base sm:text-lg font-bold text-white group-hover:text-amber-100 transition-colors"
              data-testid="resume-card-title"
            >
              {item.title || 'Continue listening'}
            </h3>
            {item.series_title && (
              <p className="truncate text-xs sm:text-sm text-slate-400">
                {item.series_title}
                {item.episode_number ? ` · Ep. ${item.episode_number}` : ''}
              </p>
            )}
            {/* Progress bar */}
            <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-slate-800">
              <div
                className="h-full rounded-full bg-gradient-to-r from-amber-400 via-rose-400 to-pink-500"
                style={{ width: `${pct}%` }}
                data-testid="resume-card-progress"
              />
            </div>
            <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500 font-mono tabular-nums">
              <span>{Math.round(pct)}% played</span>
              {remaining && <span>{remaining} left</span>}
            </div>
          </div>

          {/* Big play button */}
          <div className="flex-shrink-0">
            <span
              className="inline-flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-amber-400 text-slate-900 shadow-lg shadow-amber-500/30 transition-transform group-hover:scale-110 group-active:scale-95"
              aria-hidden="true"
            >
              <Play className="h-6 w-6 sm:h-7 sm:w-7 ml-0.5 fill-current" />
            </span>
          </div>
        </div>
      </button>
    </div>
  );
};

export default ResumeCard;
